import { CHECKOUT_ERROR_CODES } from '@iheartjane/types';
import type {
  CheckoutErrorCode,
  CheckoutErrorDetails,
} from '@iheartjane/types';

import { ApiRequestError } from './apiResponseError';

export interface TaggedCheckoutError extends Error {
  checkoutErrorDetails?: CheckoutErrorDetails;
}

interface CheckoutErrorBody {
  errors?: unknown;
}

const isCheckoutErrorCode = (code: unknown): code is CheckoutErrorCode =>
  typeof code === 'string' &&
  (Object.values(CHECKOUT_ERROR_CODES) as string[]).includes(code);

// v1 sends one object with the code beside the message. v3 Problem Details entries carry
// the code on the entry (V3::CheckoutForm#tag_error_code, no title) or nested in `detail`.
const envelopes = (errors: unknown): unknown[] =>
  Array.isArray(errors)
    ? errors.flatMap((entry) => [
        (entry as { detail?: unknown } | null)?.detail,
        entry,
      ])
    : [errors];

/**
 * Reads the details off an unwrapped error envelope. Unknown codes are ignored, so a
 * client that has not shipped copy for a new one falls back to its generic message.
 */
export const parseCheckoutErrorDetails = (
  errors: unknown
): CheckoutErrorDetails | undefined => {
  for (const envelope of envelopes(errors)) {
    const { code, title } = (envelope ?? {}) as {
      code?: unknown;
      title?: unknown;
    };

    if (isCheckoutErrorCode(code)) {
      return { code, title: typeof title === 'string' ? title : null };
    }
  }

  return undefined;
};

/**
 * Reads the details off either shape a failed checkout throws: an ApiRequestError
 * carrying the parsed body, or an Error the monolith request helper tagged.
 */
export const checkoutErrorDetails = (
  error: unknown
): CheckoutErrorDetails | undefined => {
  if (error instanceof ApiRequestError) {
    return parseCheckoutErrorDetails((error.body as CheckoutErrorBody)?.errors);
  }

  // @iheartjane/data-access's ApiError keeps the parsed envelope on `errors`.
  if (error instanceof Error && 'errors' in error) {
    const details = parseCheckoutErrorDetails(
      (error as CheckoutErrorBody).errors
    );
    if (details) return details;
  }

  return error instanceof Error
    ? (error as TaggedCheckoutError).checkoutErrorDetails
    : undefined;
};
